import React, { forwardRef, Fragment, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import useVerticals from 'data_sources/useVerticals';
import { Vertical } from 'types/vertical';

interface VerticalOption {
  value: string;
  label: string;
  vertical: Vertical;
}

export type VerticalPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  sx?: SxProps<Theme>;
};

const VerticalPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      sx,
    }: VerticalPickerProps,
    ref
  ) => {
    const verticalsQuery = useVerticals();

    const verticalOptions = useMemo(() => {
      return [
        ...(allowBlank ? [{ value: '', label: placeholder ?? '-- Select a vertical --' }] : []),
        ...(verticalsQuery.data ?? []).map((x) => {
          return { value: x.id, label: `${x.name ?? ''} (${x.abbreviation})`, jurisdiction: x };
        }),
      ];
    }, [verticalsQuery.data, allowBlank]);

    const selectedOptions = useMemo(() => {
      return verticalOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, verticalOptions]);

    return verticalsQuery.isLoading ? (
      <CircularProgress />
    ) : (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={verticalOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<VerticalOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<VerticalOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default VerticalPicker;
